import type {
  CategoryFollowedSource,
  EventFollowedType,
  FavouritesOnboardingStartedSource,
} from "~/analytics/segment";

import type { FollowFeatureType } from "./useFollow";

export const ONBOARDING_ENTRY_STORAGE_NAME = "onboardingEntry";

export type AuthDialogContentPages =
  | "login"
  | "register"
  | "forgot-password"
  | "login-with-club";

export enum OnboardingEntryType {
  Login = "login",
  Bid = "bid",
  Follow = "follow",
  AccountCreationPopup = "accountCreationPopup",
  AccountCreationSearch = "accountCreationSearch",
  ClubSso = "clubSso",
  OneTap = "oneTap",
  Giveaway = "giveaway",
}

type BaseOnboardingEntryData<T extends OnboardingEntryType, D> = {
  type: T;
  data: D &
    ({
      startingAuthPage?: AuthDialogContentPages;
      registrationTitle?: string;
      registrationSubtitle?: string;
    } | null);
};

type CategoryFollow = {
  id: string;
  name: string;
  followType: FollowFeatureType.Category;
  followSource: CategoryFollowedSource;
  source: FavouritesOnboardingStartedSource;
};

type LoginOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Login,
  { startingAuthPage?: AuthDialogContentPages } | CategoryFollow | null
>;

type GiveawayOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Giveaway,
  null
>;

type BidOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Bid,
  { bidAmount: number | null } | ({ bidAmount: number | null } & CategoryFollow)
>;

type AccountCreationPopupOnboardingEntryData = BaseOnboardingEntryData<
  | OnboardingEntryType.AccountCreationPopup
  | OnboardingEntryType.ClubSso
  | OnboardingEntryType.AccountCreationSearch,
  | CategoryFollow
  | {
      source: FavouritesOnboardingStartedSource;
    }
>;

export type FollowOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.Follow,
  | {
      id: string;
      name: string;
      followType: FollowFeatureType.Event | FollowFeatureType.Product;
      followSource: EventFollowedType;
      source: FavouritesOnboardingStartedSource;
    }
  | CategoryFollow
  | {
      source: FavouritesOnboardingStartedSource;
    }
>;

type OneTapOnboardingEntryData = BaseOnboardingEntryData<
  OnboardingEntryType.OneTap,
  null
>;

export type OnboardingEntry =
  | LoginOnboardingEntryData
  | BidOnboardingEntryData
  | FollowOnboardingEntryData
  | AccountCreationPopupOnboardingEntryData
  | OneTapOnboardingEntryData
  | GiveawayOnboardingEntryData;

const _useOnboardingDialog = () => {
  const onboardingEntry = useOnboardingStore();
  const isOnboardingDialogOpen = ref(false);

  const closeOnboardingDialog = () => {
    isOnboardingDialogOpen.value = false;
    onboardingEntry.value = undefined;
  };

  /**
   * Sets the onboarding dialog entry data in the store and triggers the dialog.
   */
  async function openOnboardingDialog(entry?: OnboardingEntry) {
    onboardingEntry.value = entry;

    await nextTick();

    isOnboardingDialogOpen.value = true;
  }

  const { bidVerified, bidLimitInActiveCurrency } = useBidder();

  const userCanPlaceTheirBid = computed(() => {
    if (
      !onboardingEntry.value ||
      onboardingEntry.value.type !== OnboardingEntryType.Bid
    )
      return false;

    if (
      onboardingEntry.value.data === undefined ||
      onboardingEntry.value.data?.bidAmount === null ||
      bidLimitInActiveCurrency.value === undefined
    ) {
      return bidVerified.value;
    }

    return (
      bidLimitInActiveCurrency.value >= onboardingEntry.value.data.bidAmount
    );
  });

  return {
    onboardingEntry,
    closeOnboardingDialog,
    openOnboardingDialog,
    userCanPlaceTheirBid,
    isOnboardingDialogOpen,
  };
};

export default createSharedComposable(_useOnboardingDialog);
